import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ChevronDown, Loader2, XCircle } from 'lucide-react'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import z from 'zod'

import { CheckboxGroup } from '@/components/CheckboxGroup'
import { Alert, AlertDescription, AlertTitle } from '@/components/elements/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/elements/collapsible'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Modal } from '@/components/Modal'
import { grantStudentCertificate } from '@/services/entity_student_certificates.service'
import { completeCourseTraining } from '@/services/student_courses.service'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

// Helper to filter valid proficiency questions (non-null, non-empty questionText)
const getValidProficiencyQuestions = (questions) =>
  questions.filter((q) => typeof q?.questionText === 'string' && q.questionText.trim() !== '')

const BulkGrantCertificatesModal = ({ event, onClose }) => {
  const { entityId } = useParams()
  const queryClient = useQueryClient()

  // Modal phase state
  const [phase, setPhase] = useState('eligibility') // 'eligibility' | 'proficiency' | 'granting'
  const [grantingResults, setGrantingResults] = useState([])

  // Determine eligible and ineligible students
  const { eligible, ineligible } = useMemo(() => {
    const studentCertificates = event.studentCertificates || []

    const eligibleStudents = studentCertificates.filter((sc) => {
      // Already has certificate
      if (sc.status === 'Issued') return false

      // Student is eligible if all their course records are completed
      // We don't require training to be complete since the event IS the training
      const courseRecords = sc.courseRecords || []
      if (courseRecords.length === 0) return false

      const allCoursesCompleted = courseRecords.every((cr) => cr.status === 'Completed')
      return allCoursesCompleted
    })

    const ineligibleStudents = studentCertificates
      .filter((sc) => {
        // Don't show already issued certificates
        if (sc.status === 'Issued') return false

        // Check if all courses are completed
        const courseRecords = sc.courseRecords || []
        if (courseRecords.length === 0) return true // No courses means not eligible

        const allCoursesCompleted = courseRecords.every((cr) => cr.status === 'Completed')
        return !allCoursesCompleted
      })
      .map((sc) => {
        // Determine ineligibility reason based on course records
        const courseRecords = sc.courseRecords || []
        if (courseRecords.length === 0) {
          return {
            ...sc,
            reason: 'Not started - student needs to be assigned and begin coursework',
          }
        }

        const completedCount = courseRecords.filter((cr) => cr.status === 'Completed').length
        const totalCount = courseRecords.length

        if (completedCount === 0) {
          return { ...sc, reason: 'Online content not started' }
        }

        return {
          ...sc,
          reason: `Online content in progress (${completedCount}/${totalCount} courses completed)`,
        }
      })

    return { eligible: eligibleStudents, ineligible: ineligibleStudents }
  }, [event.studentCertificates])

  // Group eligible students by certificate
  const certificateGroups = useMemo(() => {
    const groups = new Map()

    eligible.forEach((sc) => {
      const certId = sc.certificate.id

      if (!groups.has(certId)) {
        groups.set(certId, {
          certificate: sc.certificate,
          students: [],
          proficiencyQuestions: sc.certificate.proficiencyQuestions || [],
          hasTraining: sc.trainingStatus !== null,
        })
      }

      groups.get(certId).students.push(sc)
    })

    return groups
  }, [eligible])

  // Check if any certificates require proficiency questions
  const requiresProficiency = useMemo(
    () =>
      Array.from(certificateGroups.values()).some(
        (group) =>
          group.hasTraining && getValidProficiencyQuestions(group.proficiencyQuestions).length > 0,
      ),
    [certificateGroups],
  )

  // Guard: if the event payload didn't expand student_certificates.certificate, each
  // sc.certificate is a bare id string instead of an object. We then can't read its
  // proficiency questions, requiresProficiency silently becomes false, and we'd grant
  // without verification — the exact bug this modal shipped with. Fail loudly instead.
  const hasUnexpandedCertificate = useMemo(
    () => eligible.some((sc) => typeof sc.certificate !== 'object' || sc.certificate === null),
    [eligible],
  )

  // Form setup for proficiency questions
  const schema = useMemo(() => {
    const schemaShape = {}

    certificateGroups.forEach((group, certId) => {
      const validQuestions = getValidProficiencyQuestions(group.proficiencyQuestions)
      if (group.hasTraining && validQuestions.length > 0) {
        schemaShape[certId] = z
          .array(z.string())
          .min(
            validQuestions.length,
            `All proficiency questions must be answered for ${group.certificate.name}`,
          )
      }
    })

    return z.object(schemaShape)
  }, [certificateGroups])

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: Object.fromEntries(
      Array.from(certificateGroups.keys()).map((certId) => [certId, []]),
    ),
  })

  // Mutation for bulk granting
  const bulkGrantMutation = useMutation({
    mutationFn: async (proficiencyAnswers) => {
      const results = []

      // Initialize results
      eligible.forEach((sc) => {
        results.push({
          studentCertificate: sc,
          status: 'pending',
          error: null,
        })
      })
      setGrantingResults([...results])

      // Process all students in parallel using Promise.allSettled
      const promises = eligible.map(async (sc, index) => {
        // Update status to in-progress
        results[index].status = 'in-progress'
        setGrantingResults([...results])

        try {
          const certId = sc.certificate.id
          const proficiencyData = {
            proficiencyQuestions: proficiencyAnswers[certId] || [],
          }

          // First, mark training as complete if needed
          if (sc.trainingStatus !== 'Completed') {
            await completeCourseTraining(entityId, sc.id)
          }

          // Then grant the certificate
          await grantStudentCertificate(entityId, sc.id, proficiencyData)

          results[index].status = 'success'
          setGrantingResults([...results])
          return { success: true, studentCertificate: sc }
        } catch (error) {
          results[index].status = 'error'
          results[index].error = error.message || 'Unknown error'
          setGrantingResults([...results])
          return { success: false, studentCertificate: sc, error }
        }
      })

      const settled = await Promise.allSettled(promises)

      const successes = settled.filter(
        (result) => result.status === 'fulfilled' && result.value.success,
      )
      const failures = settled.filter(
        (result) =>
          result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success),
      )

      return { successes, failures, total: eligible.length }
    },
    onSuccess: ({ successes, failures, total }) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({
        queryKey: ['accounts', 'instructors', 'certificates'],
      })
      queryClient.invalidateQueries({
        queryKey: ['event-student-certificates', event.id],
      })

      // Show toast notification
      const successCount = successes.length
      if (successCount === total) {
        toast(`Successfully granted ${successCount} certificates`, 'success')
      } else if (successCount > 0) {
        toast(
          `Granted ${successCount} of ${total} certificates. ${failures.length} failed.`,
          'warning',
        )
      } else {
        toast('Failed to grant certificates', 'error')
      }
    },
    onError: (error) => {
      toast(error.message || 'An error occurred during bulk granting', 'error')
    },
  })

  // Phase handlers
  const handleContinue = () => {
    if (hasUnexpandedCertificate) return
    if (requiresProficiency) {
      setPhase('proficiency')
    } else {
      // Skip proficiency phase if not required
      setPhase('granting')
      bulkGrantMutation.mutate({})
    }
  }

  const handleBack = () => {
    setPhase('eligibility')
  }

  const handleGrantCertificates = (data) => {
    setPhase('granting')
    bulkGrantMutation.mutate(data)
  }

  const handleClose = () => {
    onClose()
  }

  // Prevent closing during granting
  const canClose = phase !== 'granting' || !bulkGrantMutation.isPending

  // Render different modal content based on phase
  const renderContent = () => {
    if (phase === 'eligibility') {
      return (
        <div className="flex flex-col gap-4">
          {hasUnexpandedCertificate && (
            <Alert variant="destructive">
              <AlertTitle>Certificate details didn’t load</AlertTitle>
              <AlertDescription>
                We couldn’t load full certificate information for these students, so proficiency
                questions can’t be verified. Refresh and try again — don’t grant without
                verification. If it keeps happening, contact support.
              </AlertDescription>
            </Alert>
          )}
          <EligibilityReview eligible={eligible} ineligible={ineligible} />
        </div>
      )
    }

    if (phase === 'proficiency') {
      return (
        <ProficiencyQuestions
          certificateGroups={certificateGroups}
          form={form}
          onBack={handleBack}
          onSubmit={form.handleSubmit(handleGrantCertificates)}
        />
      )
    }

    if (phase === 'granting') {
      return <GrantingProgress results={grantingResults} isPending={bulkGrantMutation.isPending} />
    }

    return null
  }

  const renderActions = () => {
    if (phase === 'eligibility') {
      return [
        {
          label: 'Cancel',
          onClick: onClose,
          type: 'cancel',
        },
        {
          label: 'Continue',
          onClick: handleContinue,
          type: 'submit',
          disabled: eligible.length === 0 || hasUnexpandedCertificate,
        },
      ]
    }

    if (phase === 'proficiency') {
      return [
        {
          label: 'Back',
          onClick: handleBack,
          type: 'cancel',
        },
        {
          label: 'Grant Certificates',
          onClick: form.handleSubmit(handleGrantCertificates),
          type: 'submit',
          disabled: !form.formState.isValid,
        },
      ]
    }

    if (phase === 'granting') {
      return [
        {
          label: 'Close',
          onClick: handleClose,
          type: 'submit',
          disabled: bulkGrantMutation.isPending,
        },
      ]
    }

    return []
  }

  const getTitle = () => {
    if (phase === 'eligibility') return 'Bulk Grant Certificates'
    if (phase === 'proficiency') return 'Proficiency Verification'
    if (phase === 'granting')
      return bulkGrantMutation.isPending ? 'Granting Certificates...' : 'Results'
    return ''
  }

  return (
    <Modal
      title={getTitle()}
      size="lg"
      content={renderContent()}
      actions={renderActions()}
      setOpen={canClose ? onClose : undefined}
      loading={false}
    />
  )
}

BulkGrantCertificatesModal.propTypes = {
  event: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default BulkGrantCertificatesModal

// Eligibility Review Phase Component
function EligibilityReview({ eligible, ineligible }) {
  const [eligibleOpen, setEligibleOpen] = useState(true)
  const [ineligibleOpen, setIneligibleOpen] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <Alert variant={eligible.length > 0 ? 'default' : 'destructive'}>
        <AlertTitle>Summary</AlertTitle>
        <AlertDescription className="inline">
          <span className="inline">
            <strong className="inline">{eligible.length}</strong> student
            {eligible.length !== 1 ? 's are' : ' is'} eligible to receive certificates.
          </span>

          {ineligible.length > 0 && (
            <span>
              <br />
              <strong className="inline">{ineligible.length}</strong> student
              {ineligible.length !== 1 ? 's need' : ' needs'} to complete requirements.
            </span>
          )}
        </AlertDescription>
      </Alert>

      {eligible.length > 0 && (
        <Collapsible open={eligibleOpen} onOpenChange={setEligibleOpen}>
          <CollapsibleTrigger className="hover:bg-accent flex w-full items-center justify-between rounded-md border p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-success h-5 w-5" />
              <span className="font-semibold">Eligible Students ({eligible.length})</span>
            </div>
            <ChevronDown
              className={`h-5 w-5 transition-transform ${eligibleOpen ? 'rotate-180' : ''}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 max-h-96 space-y-2 overflow-y-auto">
            {eligible.map((sc) => (
              <div key={sc.id} className="flex items-start gap-2 rounded-md border p-3">
                <div className="flex-1">
                  <p className="font-medium">{formatUserName(sc.student.user)}</p>
                  <p className="text-muted-foreground text-sm">{sc.certificate.name}</p>
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {ineligible.length > 0 && (
        <Collapsible open={ineligibleOpen} onOpenChange={setIneligibleOpen}>
          <CollapsibleTrigger className="hover:bg-accent flex w-full items-center justify-between rounded-md border p-4">
            <div className="flex items-center gap-2">
              <XCircle className="text-destructive h-5 w-5" />
              <span className="font-semibold">Ineligible Students ({ineligible.length})</span>
            </div>
            <ChevronDown
              className={`h-5 w-5 transition-transform ${ineligibleOpen ? 'rotate-180' : ''}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 max-h-96 space-y-2 overflow-y-auto">
            {ineligible.map((sc) => (
              <div key={sc.id} className="flex items-start gap-2 rounded-md border p-3">
                <div className="flex-1">
                  <p className="font-medium">{formatUserName(sc.student.user)}</p>
                  <p className="text-muted-foreground text-sm">{sc.certificate.name}</p>
                  <p className="text-destructive text-sm">{sc.reason}</p>
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

EligibilityReview.propTypes = {
  eligible: PropTypes.array.isRequired,
  ineligible: PropTypes.array.isRequired,
}

// Proficiency Questions Phase Component
function ProficiencyQuestions({ certificateGroups, form }) {
  return (
    <div className="flex flex-col gap-6">
      <Alert>
        <AlertTitle>Proficiency Verification</AlertTitle>
        <AlertDescription>
          Please verify proficiency for all eligible students. Questions are grouped by
          certificate.
        </AlertDescription>
      </Alert>

      <Form {...form}>
        <form className="flex flex-col gap-6">
          {Array.from(certificateGroups.entries()).map(([certId, group]) => {
            const validQuestions = getValidProficiencyQuestions(group.proficiencyQuestions)

            // Skip certificates without training or valid proficiency questions
            if (!group.hasTraining || validQuestions.length === 0) {
              return (
                <div key={certId} className="flex flex-col gap-2 rounded-lg border p-4">
                  <h3 className="text-lg font-semibold">{group.certificate.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {group.students.length} student
                    {group.students.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    This certificate does not require hands-on training proficiency verification.
                  </p>
                </div>
              )
            }

            const options = validQuestions.map((question) => ({
              id: question.id,
              label: question.questionText,
            }))

            return (
              <div key={certId} className="flex flex-col gap-4 rounded-lg border p-4">
                <div>
                  <h3 className="text-lg font-semibold">{group.certificate.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {group.students.length} student
                    {group.students.length !== 1 ? 's' : ''} will receive this certificate
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name={certId}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Proficiency Questions (all must be checked)</FormLabel>
                      <FormControl>
                        <CheckboxGroup id={`proficiency-${certId}`} options={options} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )
          })}
        </form>
      </Form>
    </div>
  )
}

ProficiencyQuestions.propTypes = {
  certificateGroups: PropTypes.instanceOf(Map).isRequired,
  form: PropTypes.object.isRequired,
}

// Granting Progress Phase Component
function GrantingProgress({ results, isPending }) {
  const successCount = results.filter((r) => r.status === 'success').length
  const failureCount = results.filter((r) => r.status === 'error').length
  const totalCount = results.length

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <div className="bg-muted h-4 w-4 rounded-full" />
      case 'in-progress':
        return <Loader2 className="text-primary h-4 w-4 animate-spin" />
      case 'success':
        return <CheckCircle2 className="text-success h-4 w-4" />
      case 'error':
        return <XCircle className="text-destructive h-4 w-4" />
      default:
        return null
    }
  }

  const getAlertVariant = () => {
    if (isPending) return 'default'
    if (failureCount === 0) return 'default' // Success
    if (successCount > 0) return 'default' // Partial success (warning styling via className)
    return 'destructive' // All failed
  }

  return (
    <div className="flex flex-col gap-4">
      {!isPending && (
        <Alert
          variant={getAlertVariant()}
          className={successCount > 0 && failureCount > 0 ? 'border-warning bg-warning/10' : ''}
        >
          <AlertTitle>
            {failureCount === 0 && 'Success!'}
            {successCount > 0 && failureCount > 0 && 'Partial Success'}
            {successCount === 0 && failureCount > 0 && 'Failed'}
          </AlertTitle>
          <AlertDescription>
            {failureCount === 0 &&
              `Successfully granted ${successCount} certificate${successCount !== 1 ? 's' : ''}.`}
            {successCount > 0 &&
              failureCount > 0 &&
              `Granted ${successCount} of ${totalCount} certificates. ${failureCount} failed.`}
            {successCount === 0 &&
              failureCount > 0 &&
              `Failed to grant ${failureCount} certificate${failureCount !== 1 ? 's' : ''}.`}
          </AlertDescription>
        </Alert>
      )}

      {isPending && (
        <div className="text-muted-foreground text-sm">
          Granting {successCount + failureCount} of {totalCount} certificates...
        </div>
      )}

      <div className="max-h-96 space-y-2 overflow-y-auto">
        {results.map((result) => (
          <div
            key={result.studentCertificate.id}
            className="flex items-start gap-3 rounded-md border p-3"
          >
            {getStatusIcon(result.status)}
            <div className="flex-1">
              <p className="font-medium">
                {formatUserName(result.studentCertificate.student.user)}
              </p>
              <p className="text-muted-foreground text-sm">
                {result.studentCertificate.certificate.name}
              </p>
              {result.error && <p className="text-destructive text-sm">{result.error}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

GrantingProgress.propTypes = {
  results: PropTypes.array.isRequired,
  isPending: PropTypes.bool.isRequired,
}
